import type { CSSVariablesResolver, MantineColorsTuple } from '@mantine/core';
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  createTheme,
  Divider,
  Drawer,
  HoverCard,
  Input,
  Kbd,
  Menu,
  Modal,
  NavLink,
  Pagination,
  Paper,
  Pill,
  Popover,
  Radio,
  RadioCard,
  RadioIndicator,
  ScrollArea,
  Select,
  Stepper,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

// Mantine resolves color variants with its own color math, which parses hex but
// not oklch, so the neutral ladder is mirrored here, keyed by stop. Same ladder
// as --color-neutral-* in src/styles/tailwind.css; change both together.
const NEUTRAL = {
  50: '#f7f7f7',
  100: '#f1f1f2',
  200: '#e4e4e6',
  300: '#c7c7ca',
  400: '#a1a2a5',
  500: '#9a9b9f',
  600: '#6b6c6f',
  700: '#56595e',
  800: '#2c2c2e',
  900: '#1e1e1e',
  950: '#0e0f11',
} as const;

const CANVAS = '#171717';
const CANVAS_RAISED = '#1a1a1b';

// Mantine's `dark` tuple runs lightest to darkest and reserves its last stops
// for surfaces rather than the darkest ink.
const darkColors: MantineColorsTuple = [
  NEUTRAL[50],
  NEUTRAL[200],
  NEUTRAL[300],
  NEUTRAL[400],
  NEUTRAL[600],
  NEUTRAL[700],
  NEUTRAL[800],
  NEUTRAL[900],
  CANVAS_RAISED,
  CANVAS,
];

const grayColors: MantineColorsTuple = [
  NEUTRAL[50],
  NEUTRAL[100],
  NEUTRAL[200],
  NEUTRAL[300],
  NEUTRAL[400],
  NEUTRAL[500],
  NEUTRAL[600],
  NEUTRAL[700],
  NEUTRAL[800],
  NEUTRAL[950],
];

/** The accent handed to the agent SDK, which cannot read CSS variables. */
export const PRIMARY_COLOR = NEUTRAL[50];

// `primaryShade` stays at Mantine's default (6 light, 8 dark) because it picks
// the shade for every palette's variants; stops 6 and 8 carry the per-scheme
// primary surface.
const primaryColors: MantineColorsTuple = [
  '#fafafa',
  NEUTRAL[100],
  NEUTRAL[200],
  NEUTRAL[300],
  NEUTRAL[400],
  NEUTRAL[500],
  NEUTRAL[900], // light scheme primary surface
  NEUTRAL[700],
  PRIMARY_COLOR, // dark scheme primary surface
  NEUTRAL[800],
];

// Categorical data-viz accent, kept separate from the theme so chart visuals
// keep their brand color regardless of the neutral primary.
const chartColors: MantineColorsTuple = [
  '#faf5ff',
  '#f3e8ff',
  '#e9d5ff',
  '#d8b4fe',
  '#c084fc',
  '#a855f7',
  '#9333ea',
  '#7e22ce',
  '#6b21a8',
  '#581c87',
];

const successColors: MantineColorsTuple = [
  '#ecfdf5',
  '#d1fae5',
  '#a7f3d0',
  '#6ee7b7',
  '#34d399',
  '#10b981',
  '#059669',
  '#047857',
  '#065f46',
  '#064e3b',
];

// Mirrors the destructive ink (stops 4/5) and fill (stops 6/8) hexes in
// tailwind.css; change together. Filled variants read stop 6 light / 8 dark.
const redColors: MantineColorsTuple = [
  '#ffebeb',
  '#ffd6d7',
  '#ffb3b5',
  '#ff8a8d',
  '#ff4245',
  '#ff383c',
  '#d4212b',
  '#c81f28',
  '#df212c',
  '#a3141c',
];

const withAlpha = (token: string, percent: number) =>
  `color-mix(in oklab, var(${token}) ${percent}%, transparent)`;

// Mantine's own stylesheet declares most of these under
// `:root[data-mantine-color-scheme]`, so the map must be returned in the
// resolver's `light`/`dark` buckets; entries in `variables` are emitted under
// bare `:root` and lose the cascade.
const semanticVariables = {
  '--mantine-color-body': 'var(--color-background)',
  '--mantine-color-text': 'var(--color-foreground)',
  '--mantine-color-bright': 'var(--color-foreground)',
  '--mantine-color-dimmed': 'var(--color-foreground-secondary)',
  '--mantine-color-placeholder': 'var(--color-foreground-tertiary)',
  '--mantine-color-anchor': 'var(--color-foreground)',
  '--mantine-color-error': 'var(--color-intent-destructive)',
  '--mantine-color-default': 'var(--color-surface-elevated)',
  '--mantine-color-default-hover': 'var(--color-fill-quaternary)',
  '--mantine-color-default-color': 'var(--color-foreground)',
  '--mantine-color-default-border': 'var(--color-line)',
  '--mantine-primary-color-filled': 'var(--color-surface-inverse)',
  '--mantine-primary-color-contrast': 'var(--color-foreground-inverse)',
  '--mantine-primary-color-light': 'var(--color-fill-quaternary)',
  '--mantine-primary-color-light-hover': 'var(--color-surface-secondary)',
  '--mantine-primary-color-light-color': 'var(--color-foreground)',
};

// Flat equivalent of compositing the scheme's heavy ink wash over the primary
// surface, for Mantine consumers that can only take one hover color.
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    ...semanticVariables,
    '--mantine-primary-color-filled-hover':
      'color-mix(in oklab, var(--color-fill-ink) 60%, var(--color-surface-inverse))',
  },
  dark: {
    ...semanticVariables,
    '--mantine-primary-color-filled-hover':
      'color-mix(in oklab, var(--color-fill-ink-inverse) 70%, var(--color-surface-inverse))',
  },
});

// Alerts tint the surface and carry the intent on border and icon; copy stays
// on the neutral foreground so it keeps contrast on either canvas.
const alertIntents: Record<string, string> = {
  red: '--color-intent-destructive',
  green: '--color-intent-success',
  yellow: '--color-intent-warning',
  blue: '--color-intent-info',
  cyan: '--color-intent-info',
};

export const theme = createTheme({
  primaryColor: 'primary',
  autoContrast: true,
  colors: {
    dark: darkColors,
    gray: grayColors,
    primary: primaryColors,
    chart: chartColors,
    success: successColors,
    red: redColors,
  },
  fontFamily: 'var(--font-sans)',
  fontFamilyMonospace: 'var(--font-mono)',
  lineHeights: {
    xs: '1.4',
    sm: '1.4',
    md: '1.4',
    lg: '1.4',
    xl: '1.4',
  },
  // TODO: Figure out how to reduce sm/xs without breaking everything
  fontSizes: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    md: '0.875rem', // 14px (default)
    lg: '1rem', // 16px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.75rem', // 28px
  },
  headings: {
    fontFamily: 'var(--font-sans)',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.4' }, // 32px
      h2: { fontSize: '1.75rem', lineHeight: '1.4' }, // 28px
      h3: { fontSize: '1.5rem', lineHeight: '1.4' }, // 24px
      h4: { fontSize: '1.375rem', lineHeight: '1.4' }, // 22px
      h5: { fontSize: '1rem', lineHeight: '1.4' }, // 16px
      h6: { fontSize: '0.875rem', lineHeight: '1.4' }, // 14px
    },
  },
  spacing: {
    xs: '0.625rem', // 10px
    sm: '0.75rem', // 12px
    md: '1rem', // 16px
    lg: '1.25rem', // 20px
    xl: '1.5rem', // 24px
  },
  // Mantine's five-step scale maps onto the three elevation roles, so a
  // `shadow` prop anywhere in the app resolves through the theme contract.
  shadows: {
    xs: 'var(--shadow-raised)',
    sm: 'var(--shadow-raised)',
    md: 'var(--shadow-floating)',
    lg: 'var(--shadow-floating)',
    xl: 'var(--shadow-overlay)',
  },
  components: {
    Title: Title.extend({
      defaultProps: {
        fw: 500,
      },
    }),
    Text: Text.extend({
      defaultProps: {
        fw: 500,
      },
    }),
    // Persistent underline on every Anchor. Mantine's default
    // `underline='hover'` removes the only affordance that an inline element
    // is clickable, which collapses links into surrounding text when the link
    // inherits its parent color (Sources row source-name, alert links, modal
    // links, markdown links via MantineMarkdown's `a` override). Explicit
    // props on call sites still win.
    Anchor: Anchor.extend({
      defaultProps: {
        underline: 'always',
      },
    }),
    // Mantine draws every line from one palette stop, so these split it by
    // purpose: surface outlines take `line`, while visible rules take
    // `line-strong`.
    Paper: Paper.extend({
      defaultProps: {
        bg: 'var(--color-surface-elevated)',
      },
      styles: {
        root: {
          borderColor: 'var(--color-line)',
        },
      },
    }),
    Card: Card.extend({
      defaultProps: {
        bg: 'var(--color-surface-elevated)',
      },
      styles: {
        root: {
          borderColor: 'var(--color-line)',
        },
        section: {
          borderColor: 'var(--color-line-strong)',
        },
      },
    }),
    Divider: Divider.extend({
      styles: {
        root: {
          '--divider-color': 'var(--color-line-strong)',
        },
      },
    }),
    Table: Table.extend({
      styles: {
        table: {
          '--table-border-color': 'var(--color-line-strong)',
        },
      },
    }),
    Accordion: Accordion.extend({
      styles: {
        item: {
          '--item-border-color': 'var(--color-line-strong)',
        },
      },
    }),
    Kbd: Kbd.extend({
      styles: {
        root: {
          borderColor: 'var(--color-line-strong)',
        },
      },
    }),
    ScrollArea: ScrollArea.extend({
      defaultProps: {
        scrollbarSize: 6,
      },
      styles: {
        thumb: {
          backgroundColor: 'var(--color-line-strong)',
        },
      },
    }),
    // Matches the Design System input surface across Mantine's input family.
    // These resolve as inline styles and must stay on the wrapper: on the input
    // element they outrank Mantine's own `:focus` and `[data-error]` border swaps.
    // Unstyled controls stay transparent so they inherit their container surface.
    Input: Input.extend({
      styles: (_theme, props) => ({
        wrapper:
          props.variant === 'unstyled'
            ? {
                '--input-bg': 'transparent',
                '--input-disabled-bg': 'transparent',
                '--input-bd': 'transparent',
                '--input-bd-focus': 'transparent',
              }
            : {
                '--input-bg': 'var(--color-fill-quaternary)',
                '--input-disabled-bg': 'transparent',
                '--input-disabled-color': 'var(--color-foreground-tertiary)',
                '--input-bd': props.error
                  ? 'var(--mantine-color-error)'
                  : 'var(--color-line-strong)',
                '--input-bd-focus': 'var(--color-ring-input)',
              },
      }),
    }),
    Select: Select.extend({
      defaultProps: {
        radius: 'md',
        withCheckIcon: false,
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        radius: 'sm',
        fw: 500,
        tt: 'none',
        variant: 'light',
        size: 'md',
      },
      // Only neutral badges are re-surfaced; intent and tag colors carry
      // meaning in their hue.
      styles: (_theme, props) => {
        const isNeutral =
          !props.color || props.color === 'primary' || props.color === 'gray';
        if (props.variant !== 'light' || !isNeutral) return {};

        return {
          root: {
            backgroundColor: 'var(--color-fill-quaternary)',
            color: 'var(--color-foreground)',
            border: '1px solid var(--color-line-strong)',
          },
        };
      },
    }),
    Pill: Pill.extend({
      defaultProps: {
        size: 'md',
      },
    }),
    Avatar: Avatar.extend({
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          backgroundColor: 'var(--color-surface-secondary)',
          outline: '1px solid var(--color-line)',
          outlineOffset: -1,
        },
        placeholder: {
          color: 'var(--color-foreground)',
          fontWeight: 600,
        },
      },
    }),
    // Mantine React Table and @mantine/dates render Mantine buttons of their
    // own, so these two blocks stay reachable with no first-party call site.
    Button: Button.extend({
      defaultProps: {
        radius: 'md',
      },
      styles: (_theme, props) => {
        const isPrimaryColor = !props.color || props.color === 'primary';
        const isFilled =
          props.variant === 'filled' || props.variant === undefined;
        const isDefault = props.variant === 'default';
        const isLight = props.variant === 'light';

        if (isFilled && isPrimaryColor) {
          return {
            root: {
              backgroundColor: 'var(--color-surface-inverse)',
              color: 'var(--color-foreground-inverse)',
              '&:hover:not([data-disabled])': {
                // Pins the base fill against Mantine's own filled-hover
                // variable, then composites the wash over it.
                backgroundColor: 'var(--color-surface-inverse)',
                backgroundImage:
                  'linear-gradient(var(--color-fill-secondary), var(--color-fill-secondary))',
                color: 'var(--color-foreground-inverse)',
              },
              '&[data-disabled]': {
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-foreground-tertiary)',
              },
            },
          };
        }
        if (isLight && isPrimaryColor) {
          return {
            root: {
              backgroundColor: 'var(--color-fill-quaternary)',
              color: 'var(--color-foreground)',
              borderColor: 'var(--color-line-strong)',
              '&:hover:not([data-disabled])': {
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-foreground)',
              },
            },
          };
        }
        if ((isFilled || isDefault) && props.color === 'gray') {
          // color="gray" + filled = the secondary action surface.
          return {
            root: {
              backgroundColor: 'var(--color-fill-quaternary)',
              color: 'var(--color-foreground)',
              borderColor: 'var(--color-line-strong)',
              '&:hover:not([data-disabled])': {
                backgroundColor: 'var(--color-fill-quaternary)',
                backgroundImage:
                  'linear-gradient(var(--color-fill-quaternary), var(--color-fill-quaternary))',
                color: 'var(--color-foreground)',
              },
              '&[data-disabled]': {
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-foreground-tertiary)',
              },
            },
          };
        }
        return {};
      },
    }),
    ActionIcon: ActionIcon.extend({
      styles: (_theme, props) => {
        const isPrimaryColor = props.color === 'primary';
        const isFilled =
          props.variant === 'filled' || props.variant === undefined;

        if (isFilled && isPrimaryColor) {
          return {
            root: {
              backgroundColor: 'var(--color-surface-inverse)',
              color: 'var(--color-foreground-inverse)',
              '&:hover:not([data-disabled])': {
                // Pins the base fill against Mantine's own filled-hover
                // variable, then composites the wash over it.
                backgroundColor: 'var(--color-surface-inverse)',
                backgroundImage:
                  'linear-gradient(var(--color-fill-secondary), var(--color-fill-secondary))',
                color: 'var(--color-foreground-inverse)',
              },
              '&[data-disabled]': {
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-foreground-tertiary)',
              },
            },
          };
        }
        if (isFilled && props.color === 'gray') {
          return {
            root: {
              backgroundColor: 'var(--color-fill-quaternary)',
              color: 'var(--color-foreground)',
              borderColor: 'var(--color-line-strong)',
              '&:hover:not([data-disabled])': {
                backgroundColor: 'var(--color-fill-quaternary)',
                backgroundImage:
                  'linear-gradient(var(--color-fill-quaternary), var(--color-fill-quaternary))',
                color: 'var(--color-foreground)',
              },
              '&[data-disabled]': {
                backgroundColor: 'var(--color-surface-secondary)',
                color: 'var(--color-foreground-tertiary)',
              },
            },
          };
        }
        // Mantine's filled foreground falls back to white, which the light
        // primary surface hides. It has to be set as a property, not through
        // --ai-color, because the variant resolver writes that one inline on
        // every render and an inline value outranks anything set here.
        if (isFilled && !props.color) {
          return {
            root: {
              color: 'var(--color-foreground-inverse)',
              '&:hover:not([data-disabled])': {
                color: 'var(--color-foreground-inverse)',
              },
            },
          };
        }
        return {};
      },
    }),
    // Mantine's active-page foreground must follow the primary surface.
    Pagination: Pagination.extend({
      styles: {
        control: {
          borderColor: 'var(--color-line)',
          '--pagination-active-color': 'var(--color-foreground-inverse)',
        },
      },
    }),
    // The checked surface is the primary, so its check has to be the paired
    // primary foreground rather than Mantine's default white.
    Checkbox: Checkbox.extend({
      defaultProps: {
        iconColor: 'var(--color-foreground-inverse)',
      },
    }),
    Radio: Radio.extend({
      defaultProps: {
        iconColor: 'var(--color-foreground-inverse)',
      },
    }),
    RadioCard: RadioCard.extend({
      defaultProps: {
        withBorder: false,
      },
      styles: {
        card: {
          boxShadow: 'var(--shadow-raised)',
          '&&': {
            backgroundColor: 'var(--color-surface-elevated)',
          },
        },
      },
    }),
    // Radio.Indicator is its own component and does not inherit Radio's
    // defaults.
    RadioIndicator: RadioIndicator.extend({
      defaultProps: {
        iconColor: 'var(--color-foreground-inverse)',
      },
    }),
    // Mantine derives the step icon's foreground from the light-scheme primary shade, so it needs the paired inverse foreground to stay legible on the primary surface.
    Stepper: Stepper.extend({
      vars: () => ({
        root: {
          '--stepper-icon-color': 'var(--color-foreground-inverse)',
        },
      }),
    }),
    // A single thumb color fails on one end: the unchecked track and the
    // checked primary sit at opposite luminances.
    Switch: Switch.extend({
      styles: {
        thumb: {
          backgroundColor: 'var(--color-surface-elevated)',
          borderColor: 'var(--color-surface-elevated)',
          '[data-checked] &': {
            backgroundColor: 'var(--color-foreground-inverse)',
            borderColor: 'var(--color-foreground-inverse)',
          },
        },
      },
    }),
    Alert: Alert.extend({
      defaultProps: {
        radius: 'sm',
      },
      styles: (theme, props) => {
        const intent = props.color ? alertIntents[props.color] : undefined;
        const surface = intent
          ? withAlpha(intent, 12)
          : 'var(--color-surface-secondary)';
        const border = intent
          ? withAlpha(intent, 30)
          : 'var(--color-line-strong)';
        const accent = intent ? `var(${intent})` : 'var(--color-foreground)';

        return {
          root: {
            backgroundColor: surface,
            border: `1px solid ${border}`,
            padding: '8px 8px 8px 12px',
          },
          icon: {
            color: accent,
            width: '16px',
            height: '16px',
            marginRight: '12px',
          },
          title: {
            color: 'var(--color-foreground)',
            fontSize: theme.fontSizes.sm,
            fontWeight: 600,
            marginBottom: 0,
          },
          message: {
            color: 'var(--color-foreground-secondary)',
            fontSize: theme.fontSizes.sm,
            fontWeight: 500,
          },
        };
      },
    }),
    Menu: Menu.extend({
      styles: (theme) => ({
        dropdown: {
          backgroundColor: 'var(--color-surface-elevated-secondary)',
          border: '1px solid var(--color-line)',
          boxShadow: 'var(--shadow-floating)',
        },
        item: {
          color: 'var(--color-foreground)',
          fontSize: theme.fontSizes.sm,
          '& .mantine-Menu-itemSection': {
            color: 'var(--color-foreground-secondary)',
          },
          '&:hover': {
            backgroundColor: 'var(--color-fill-quaternary)',
            color: 'var(--color-foreground)',
            '& .mantine-Menu-itemSection': {
              color: 'var(--color-foreground)',
            },
          },
          '&[data-disabled]': {
            color: 'var(--color-foreground-tertiary)',
          },
        },
        divider: {
          borderColor: 'var(--color-line)',
        },
      }),
    }),
    Popover: Popover.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--color-surface-elevated-secondary)',
          color: 'var(--color-foreground)',
          border: '1px solid var(--color-line)',
          boxShadow: 'var(--shadow-floating)',
        },
      },
    }),
    HoverCard: HoverCard.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--color-surface-elevated-secondary)',
          color: 'var(--color-foreground)',
          border: '1px solid var(--color-line)',
          boxShadow: 'var(--shadow-floating)',
        },
      },
    }),
    Tooltip: Tooltip.extend({
      styles: {
        tooltip: {
          backgroundColor: 'var(--color-surface-inverse)',
          color: 'var(--color-foreground-inverse)',
        },
      },
    }),
    NavLink: NavLink.extend({
      styles: {
        root: {
          color: 'var(--color-foreground-secondary)',
          '& .mantine-NavLink-label': {
            color: 'inherit',
          },
          '& .mantine-NavLink-section': {
            color: 'var(--color-foreground-tertiary)',
          },
          '& .mantine-NavLink-section svg': {
            color: 'var(--color-foreground-tertiary)',
          },
          '&:hover:not([data-active]), &[data-active]': {
            backgroundColor: 'var(--color-fill-quaternary)',
            color: 'var(--color-foreground)',
            '& .mantine-NavLink-label': {
              color: 'var(--color-foreground)',
            },
            '& .mantine-NavLink-section': {
              color: 'var(--color-foreground)',
            },
            '& .mantine-NavLink-section svg': {
              color: 'var(--color-foreground)',
            },
          },
        },
      },
    }),
    Drawer: Drawer.extend({
      styles: {
        overlay: {
          backgroundColor: 'var(--color-overlay)',
        },
        // An overlay's content is a Paper and takes its surface; the sections
        // inherit it so a header can never become a second surface.
        content: {
          color: 'var(--color-foreground)',
          boxShadow: 'var(--shadow-overlay)',
        },
        header: {
          backgroundColor: 'inherit',
        },
        body: {
          backgroundColor: 'inherit',
        },
      },
    }),
    Modal: Modal.extend({
      styles: {
        overlay: {
          backgroundColor: 'var(--color-overlay)',
        },
        content: {
          color: 'var(--color-foreground)',
          borderRadius: 12,
          border: '1px solid var(--color-line)',
          boxShadow: 'var(--shadow-overlay)',
        },
        header: {
          backgroundColor: 'inherit',
        },
        body: {
          backgroundColor: 'inherit',
        },
      },
    }),
  },
});
